import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Link, Loader, Package, CheckCircle, AlertCircle, ExternalLink, Smartphone, Laptop, Tablet, RefreshCw, ChevronDown } from 'lucide-react';

const CATEGORIES = [
  { value: 'Mobile Phones', label: 'Mobile Phones', icon: Smartphone, urlPath: 'mobiles' },
  { value: 'Laptops', label: 'Laptops', icon: Laptop, urlPath: 'laptops' },
  { value: 'Tablets', label: 'Tablets', icon: Tablet, urlPath: 'tablets' },
];

const EXAMPLE_URLS = {
  'Mobile Phones': [
    'https://www.smartprix.com/mobiles/apple-iphone-16-pro-ppd1fv0j3mwy',
    'https://www.smartprix.com/mobiles/samsung-galaxy-s24-ppd1frlkgf4v'
  ],
  'Laptops': [
    'https://www.smartprix.com/laptops/dell-inspiron-5440-laptop-13th-gen-core-i5-ppd1ouj4gx6l',
    'https://www.smartprix.com/laptops/asus-expertbook-p1-14-p1403cva-s60938ws-laptop-ppd1g4dms6o7'
  ],
  'Tablets': [
    'https://www.smartprix.com/tablets/apple-ipad-11th-gen-2025-5g-ppd1jb60rwu6',
    'https://www.smartprix.com/tablets/samsung-galaxy-tab-s10-ultra-5g-ppd1hnlz2blt'
  ]
};

const ProductScrapeModal = ({ isOpen, onClose, category: initialCategory, onSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Input, 2: Preview, 3: Brand Logo Confirmation (if new brand)
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'Mobile Phones');
  const [productUrl, setProductUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [customLogoUrl, setCustomLogoUrl] = useState('');
  const [useCustomLogo, setUseCustomLogo] = useState(false);

  // Update selected category when prop changes
  useEffect(() => {
    if (initialCategory) {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);

  // Get category config
  const getCategoryConfig = () => {
    return CATEGORIES.find(c => c.value === selectedCategory) || CATEGORIES[0];
  };

  const categoryConfig = getCategoryConfig();
  const sourceLabel = 'Smartprix';
  const sourceUrl = `https://www.smartprix.com/${categoryConfig.urlPath}`;
  const sourceDomain = 'smartprix.com';
  const exampleUrls = EXAMPLE_URLS[selectedCategory] || EXAMPLE_URLS['Mobile Phones'];

  const getCategoryIcon = (size = 12) => {
    const IconComponent = categoryConfig.icon;
    return <IconComponent className={`w-${size} h-${size}`} />;
  };

  const getCategoryLabel = () => {
    if (selectedCategory === 'Laptops') return 'laptop';
    if (selectedCategory === 'Tablets') return 'tablet';
    return 'phone';
  };

  // Handle category change
  const handleCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory);
    setProductUrl(''); // Clear URL when category changes
    setError('');
    setPreviewData(null);
  };

  const handlePreview = async () => {
    if (!productUrl.trim()) {
      setError(`Please enter ${sourceLabel} URL`);
      return;
    }

    if (!productUrl.includes(sourceDomain)) {
      setError(`Invalid URL. Please provide a valid ${sourceLabel} URL`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Use universal scraper endpoint for all categories
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scrape/preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ url: productUrl })
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (data.product_exists) {
          setError(`This product already exists in the system (SKU: ${data.product?.sku_code || data.product?.sku_id})`);
        } else {
          setPreviewData(data);
          // If new brand will be created, go to logo confirmation step
          if (data.brand?.will_auto_create) {
            setCustomLogoUrl(data.brand?.logo_url || '');
            setStep(3); // Brand logo confirmation step
          } else {
            setStep(2); // Regular preview step
          }
        }
      } else {
        setError(data.detail || 'Failed to fetch product preview');
      }
    } catch (err) {
      console.error('Error fetching preview:', err);
      setError('Failed to fetch product preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = async () => {
    try {
      setLoading(true);
      setError('');

      // Build request body with optional custom logo
      const requestBody = { url: productUrl };
      if (useCustomLogo && customLogoUrl) {
        requestBody.custom_brand_logo = customLogoUrl;
      }

      // Use universal scraper endpoint for all categories
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scrape/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestBody)
        }
      );

      const data = await response.json();

      if (response.ok) {
        const productId = data.product?.id || data.product_id;
        const sku = data.product?.sku_code || data.product?.sku_id || data.sku_id;
        const brand = data.product?.brand || data.brand;
        const model = data.product?.model_name || data.product?.model || data.model;
        const variants = data.variants_created || data.zupc_variants?.length || 0;
        
        // Show success message
        alert(`✓ Product added successfully!\n\nSKU: ${sku}\nBrand: ${brand}\nModel: ${model}\nVariants Created: ${variants}`);
        
        // Navigate to the product detail page with full page refresh
        if (productId) {
          // Use window.location for full page refresh to ensure fresh data
          window.location.href = `/products/${productId}`;
        } else {
          // Fallback: just refresh products list
          onSuccess();
          handleClose();
        }
      } else {
        setError(data.detail || 'Failed to add product');
      }
    } catch (err) {
      console.error('Error adding product:', err);
      setError('Failed to add product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle brand logo confirmation - proceed to preview
  const handleLogoConfirmed = () => {
    setStep(2);
  };

  const handleClose = () => {
    setStep(1);
    setSelectedCategory(initialCategory || 'Mobile Phones');
    setProductUrl('');
    setError('');
    setPreviewData(null);
    setCustomLogoUrl('');
    setUseCustomLogo(false);
    onClose();
  };

  const handleBack = () => {
    if (step === 3) {
      // From brand logo confirmation, go back to URL input
      setStep(1);
      setPreviewData(null);
    } else if (step === 2) {
      // From preview, check if we came from logo confirmation
      if (previewData?.brand?.will_auto_create) {
        setStep(3); // Go back to logo confirmation
      } else {
        setStep(1);
        setPreviewData(null);
      }
    }
    setError('');
  };

  if (!isOpen) return null;

  // Get product details from preview data (handle both mobile and laptop formats)
  const getPreviewProduct = () => {
    if (!previewData) return null;
    
    // Get storage options - this is the ZUPC count (one ZUPC per storage variant)
    const storageOptions = previewData.attributes?.storage_options || previewData.product?.memory_variants || [];
    const colorOptions = previewData.attributes?.color_options || previewData.product?.colors || [];
    
    return {
      name: previewData.product?.model_name || previewData.product?.full_name || previewData.product?.model,
      sku: previewData.product?.sku_code || previewData.product?.sku_id,
      brandName: previewData.brand?.name || previewData.product?.brand,
      brandWillCreate: previewData.brand?.will_auto_create,
      brandLogo: previewData.brand?.logo_url,
      brandExists: previewData.brand?.exists,
      images: previewData.product?.images || [],
      colors: colorOptions,
      variants: storageOptions,
      ramOptions: previewData.attributes?.ram_options || [],
      // Store prices for MRP display
      storePrices: previewData.store_prices || [],
      features: previewData.features || {},
      stats: {
        images: previewData.stats?.total_images || previewData.product?.images?.length || 0,
        specs: previewData.stats?.specifications_count || Object.keys(previewData.product?.specifications || {}).length || 0,
        colors: colorOptions.length,
        // ZUPC count = number of storage variants (NOT storage × color)
        zupcs: storageOptions.length
      }
    };
  };

  const product = getPreviewProduct();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-[#2b3380] px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Package className="w-5 h-5" />
              {step === 1 ? `Add Product from ${sourceLabel}` : 
               step === 3 ? 'Confirm Brand Logo' : 'Product Preview'}
            </h3>
            <button onClick={handleClose} className="text-white hover:text-white/80">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
            {step === 1 ? (
              /* Step 1: URL Input */
              <div className="space-y-4">
                {/* Category Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <div className="relative">
                    <select
                      value={selectedCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md bg-white text-gray-800 focus:ring-2 focus:ring-[#2b3380] focus:border-transparent appearance-none cursor-pointer"
                      data-testid="category-select"
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* URL Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {sourceLabel} URL <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="url"
                        value={productUrl}
                        onChange={(e) => setProductUrl(e.target.value)}
                        placeholder={exampleUrls[0]}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                        autoFocus
                        data-testid="product-url-input"
                      />
                    </div>
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-1"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open
                    </a>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Go to {sourceLabel}, find your {getCategoryLabel()}, and paste the URL here
                  </p>
                </div>

                {/* Example URLs */}
                <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                  <p className="text-sm font-medium text-blue-800 mb-2">Example URLs:</p>
                  <div className="space-y-1">
                    {exampleUrls.map((url, idx) => (
                      <code key={idx} className="block text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded truncate">
                        {url}
                      </code>
                    ))}
                  </div>
                </div>

                {/* What Gets Created */}
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded">
                  <p className="text-sm font-medium text-emerald-800 mb-2">✨ What gets auto-created:</p>
                  <ul className="text-xs text-emerald-700 space-y-1 ml-4 list-disc">
                    <li>Brand (if not exists) with logo</li>
                    <li>SKU with all specifications</li>
                    {selectedCategory === 'Laptops' ? (
                      <>
                        <li>ZUPC variants (Processor configurations)</li>
                        <li>Attributes (RAM, Storage, Color options)</li>
                      </>
                    ) : (
                      <li>All ZUPC variants (Storage × Color combinations)</li>
                    )}
                    <li>Images (watermarks removed, optimized)</li>
                  </ul>
                  <p className="text-xs text-emerald-600 mt-2 italic">💡 Pricing (MRP, FSP, MBP) can be added later from the Product Detail page</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className={`${error.includes('redirect') || error.includes('mismatch') ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'} border px-4 py-3 rounded`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${error.includes('redirect') || error.includes('mismatch') ? 'text-amber-600' : 'text-red-600'}`} />
                      <div className="flex-1">
                        <span className={error.includes('redirect') || error.includes('mismatch') ? 'text-amber-800' : 'text-red-700'}>{error}</span>
                        {(error.includes('redirect') || error.includes('mismatch') || error.includes('outdated')) && (
                          <div className="mt-2 p-2 bg-white rounded border border-amber-200">
                            <p className="text-sm font-medium text-amber-800 mb-1">💡 How to fix:</p>
                            <ol className="text-xs text-amber-700 space-y-1 list-decimal ml-4">
                              <li>Go to <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="underline">{sourceDomain}</a></li>
                              <li>Search for your device using the search bar</li>
                              <li>Copy the URL from the correct product page</li>
                              <li>Paste the new URL here and try again</li>
                            </ol>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : step === 2 ? (
              /* Step 2: Preview */
              <div className="space-y-4">
                {product && (
                  <>
                    {/* Product Info */}
                    <div className="flex gap-4">
                      {/* Images Preview */}
                      <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt="Product"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            {getCategoryIcon()}
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900">{product.name}</h4>
                        <p className="text-sm text-gray-600">SKU: {product.sku}</p>
                        
                        {/* Brand Status with Logo */}
                        <div className="mt-2 flex items-center gap-2">
                          {product.brandLogo ? (
                            <img src={product.brandLogo} alt={product.brandName} className="w-8 h-8 object-contain bg-white rounded border p-0.5" />
                          ) : (
                            <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center text-xs font-bold text-gray-500">
                              {product.brandName?.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <span className="text-sm text-gray-600">Brand: <strong>{product.brandName}</strong></span>
                          {product.brandWillCreate ? (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded font-medium">New - Will Create</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">✓ Exists</span>
                          )}
                        </div>
                        
                        {/* Brand logo source info for new brands */}
                        {product.brandWillCreate && (
                          <p className="text-xs text-gray-500 mt-1 ml-10">
                            Logo: {product.brandLogo?.includes('wikipedia') ? '✓ Wikipedia Commons' : 
                                   product.brandLogo?.includes('ui-avatars') ? '⚠ Generated (fallback)' : 'Auto-detected'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-blue-50 p-3 rounded text-center">
                        <p className="text-2xl font-bold text-blue-700">{product.stats.images}</p>
                        <p className="text-xs text-blue-600">Images</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded text-center">
                        <p className="text-2xl font-bold text-purple-700">{product.stats.specs}</p>
                        <p className="text-xs text-purple-600">Specs</p>
                      </div>
                      <div className="bg-emerald-50 p-3 rounded text-center">
                        <p className="text-2xl font-bold text-emerald-700">{product.stats.colors}</p>
                        <p className="text-xs text-emerald-600">Colors</p>
                      </div>
                      <div className="bg-orange-50 p-3 rounded text-center">
                        <p className="text-2xl font-bold text-orange-700">{product.stats.zupcs}</p>
                        <p className="text-xs text-orange-600">ZUPCs</p>
                      </div>
                    </div>
                    
                    {/* MRP / Store Prices */}
                    {product.storePrices?.length > 0 && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">💰 MRP (Store Prices):</p>
                        <div className="flex flex-wrap gap-3">
                          {product.storePrices.map((sp, idx) => (
                            <div key={idx} className="bg-white px-3 py-2 rounded border border-green-200 shadow-sm">
                              <span className="text-xs text-gray-500 block">{sp.store_name}</span>
                              <span className="text-lg font-bold text-green-700">₹{sp.price?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Colors (Precise) */}
                    {product.colors?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">🎨 Available Colors ({product.colors.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {product.colors.map((color, idx) => (
                            <span key={idx} className="text-xs px-2.5 py-1 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-full font-medium">
                              {color}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Storage/Memory Variants (ZUPCs) */}
                    {product.variants?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">📦 Storage Variants ({product.variants.length} ZUPCs will be created):</p>
                        <div className="flex flex-wrap gap-2">
                          {product.variants.map((variant, idx) => (
                            <span key={idx} className="text-xs px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg font-medium border border-blue-200">
                              {variant}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Each ZUPC will have all {product.colors?.length || 0} colors as available attributes
                        </p>
                      </div>
                    )}

                    {/* RAM Options (Laptops only) */}
                    {selectedCategory === 'Laptops' && product.ramOptions?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">RAM Options:</p>
                        <div className="flex flex-wrap gap-2">
                          {product.ramOptions.map((ram, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {ram}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Error */}
                    {error && (
                      <div className="bg-red-50 border border-red-200 px-4 py-3 rounded">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-600" />
                          <span className="text-red-700">{error}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : step === 3 ? (
              /* Step 3: Brand Logo Confirmation (New Brand) */
              <div className="space-y-6">
                <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
                    <div>
                      <p className="text-amber-800 font-medium">New Brand Detected</p>
                      <p className="text-amber-700 text-sm mt-1">
                        The brand "<strong>{product?.brandName}</strong>" doesn't exist in the system. 
                        It will be auto-created when you add this product.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Brand Logo Preview */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-4">Brand Logo Preview</p>
                  
                  <div className="flex items-center gap-6">
                    {/* Current Logo */}
                    <div className="text-center">
                      <div className="w-24 h-24 bg-white rounded-xl border-2 border-gray-200 flex items-center justify-center overflow-hidden shadow-sm">
                        {(useCustomLogo ? customLogoUrl : product?.brandLogo) ? (
                          <img 
                            src={useCustomLogo ? customLogoUrl : product?.brandLogo} 
                            alt={product?.brandName} 
                            className="w-20 h-20 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="w-20 h-20 bg-gradient-to-br from-[#2b3380] to-[#1e2560] rounded-lg flex items-center justify-center text-white text-2xl font-bold" style={{ display: (useCustomLogo ? customLogoUrl : product?.brandLogo) ? 'none' : 'flex' }}>
                          {product?.brandName?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {useCustomLogo ? 'Custom Logo' : 'Auto-fetched'}
                      </p>
                    </div>

                    {/* Brand Info */}
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-800">{product?.brandName}</h4>
                      <p className="text-sm text-gray-500 mt-1">New brand will be created</p>
                      
                      {/* Logo Options */}
                      <div className="mt-4 space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="logoOption"
                            checked={!useCustomLogo}
                            onChange={() => setUseCustomLogo(false)}
                            className="w-4 h-4 text-[#2b3380]"
                          />
                          <span className="text-sm text-gray-700">Use auto-fetched logo</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="logoOption"
                            checked={useCustomLogo}
                            onChange={() => setUseCustomLogo(true)}
                            className="w-4 h-4 text-[#2b3380]"
                          />
                          <span className="text-sm text-gray-700">Use custom logo URL</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Custom Logo URL Input */}
                  {useCustomLogo && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Logo URL
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={customLogoUrl}
                          onChange={(e) => setCustomLogoUrl(e.target.value)}
                          placeholder="https://example.com/logo.png"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#2b3380] focus:border-[#2b3380] text-sm"
                        />
                        <button
                          onClick={() => setCustomLogoUrl(product?.brandLogo || '')}
                          className="px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          title="Reset to auto-fetched"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Enter a direct URL to the brand logo image (PNG, JPG, SVG)
                      </p>
                    </div>
                  )}
                </div>

                {/* Product Summary */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <p className="text-sm font-medium text-blue-800 mb-2">Product to be added:</p>
                  <div className="flex items-center gap-3">
                    {product?.images?.[0] && (
                      <img src={product.images[0]} alt="" className="w-12 h-12 object-contain rounded" />
                    )}
                    <div>
                      <p className="font-medium text-gray-800">{product?.name}</p>
                      <p className="text-sm text-gray-600">{product?.variants?.length || 0} variants · {product?.colors?.length || 0} colors</p>
                    </div>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 px-4 py-3 rounded">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-600" />
                      <span className="text-red-700">{error}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between items-center">
            {(step === 2 || step === 3) && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
            )}
            <div className={step === 1 ? 'ml-auto' : ''}>
              {step === 1 ? (
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="px-4 py-2 bg-[#2b3380] text-white rounded-md hover:bg-[#1e2560] disabled:opacity-50 flex items-center gap-2"
                  data-testid="fetch-preview-btn"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4" />
                      Fetch Preview
                    </>
                  )}
                </button>
              ) : step === 3 ? (
                <button
                  onClick={handleLogoConfirmed}
                  className="px-4 py-2 bg-[#2b3380] text-white rounded-md hover:bg-[#1e2560] flex items-center gap-2"
                  data-testid="confirm-logo-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Confirm & Continue
                </button>
              ) : (
                <button
                  onClick={handleProceed}
                  disabled={loading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                  data-testid="add-product-btn"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Add Product
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductScrapeModal;
